import React from "react";
import {
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Typography,
  Flex,
  Popover,
  Space,
} from "antd";
import { DeleteOutlined, PlusOutlined, InfoCircleOutlined, MinusCircleOutlined, EditOutlined } from "@ant-design/icons";
import styles from "./plan-editor.module.css";
import { labelToKey } from "Utilities/featureSummaryHelper";
import {
  ISubscriptionTier,
  CardFeatureList,
} from "Features/SubscriptionConfiguration/SubscriptionTiers/redux/types";

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

interface PlanEditorProps {
  currentPlan: ISubscriptionTier;
  onSave?: (plan: ISubscriptionTier) => Promise<void> | void;
}

const PlanEditor: React.FC<PlanEditorProps> = ({
  currentPlan,
}: PlanEditorProps) => {
  const form = Form.useFormInstance();

  React.useEffect(() => {
    if (currentPlan) {
      let features: CardFeatureList[] = [];

      // Deserialize features from description field
      if (currentPlan.featureSummary) {
        try {
          features = JSON.parse(currentPlan.featureSummary);
        } catch (error) {
          console.warn("Failed to parse features from description:", error);
          features = [];
        }
      }

      form.setFieldsValue({
        featureSummaryList: features,
      });
    }
  }, [currentPlan]);

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        <Title level={4}>
          Please add the text that will show on the subscription card
        </Title>
        <Form.List name="featureSummaryList">
          {(fields, { add, remove }) => (
            <>
              <Flex justify="space-between" align="center">
                <Title level={5}>Features</Title>
                <Button
                  type="dashed"
                  onClick={() => add({ key: "", label: "", state: "included" })}
                  icon={<PlusOutlined />}
                >
                  Add Feature
                </Button>
              </Flex>

              {fields.map(({ key, name, ...restField }) => (
                <div key={key} className={styles.featureRow}>
                  <Form.Item
                    {...restField}
                    name={[name, "label"]}
                    rules={[
                      {
                        required: true,
                        message: "Feature label is required",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Feature label"
                      onChange={(e) => {
                        const label = e.target.value;
                        const keyValue = labelToKey(label);
                        form.setFieldValue(
                          ["featureSummaryList", name, "key"],
                          keyValue
                        );
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    {...restField}
                    name={[name, "state"]}
                    rules={[
                      {
                        required: true,
                        message: "Feature state is required",
                      },
                    ]}
                  >
                    <Select placeholder="Select state">
                      <Option value="included">Included</Option>
                      <Option value="limited">Limited</Option>
                      <Option value="not_included">Not Included</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, curValues) =>
                      prevValues.featureSummaryList?.[name]?.state !==
                      curValues.featureSummaryList?.[name]?.state
                    }
                  >
                    {({ getFieldValue }) => {
                      const state = getFieldValue([
                        "featureSummaryList",
                        name,
                        "state",
                      ]);
                      if (state === "limited") {
                        return (
                          <>
                            <Form.Item
                              {...restField}
                              name={[name, "value"]}
                              rules={[
                                {
                                  required: true,
                                  message:
                                    "Value is required for limited features",
                                },
                              ]}
                            >
                              <Input placeholder="Value" />
                            </Form.Item>
                          </>
                        );
                      }
                      return null;
                    }}
                  </Form.Item>

                  <Form.Item {...restField} name={[name, "order"]}>
                    <InputNumber
                      placeholder="Order"
                      style={{ width: "100%" }}
                    />
                  </Form.Item>

                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(name)}
                  />

                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, curValues) =>
                      prevValues.featureSummaryList?.[name]?.descriptionFormat !==
                      curValues.featureSummaryList?.[name]?.descriptionFormat ||
                      prevValues.featureSummaryList?.[name]?.description !==
                      curValues.featureSummaryList?.[name]?.description ||
                      prevValues.featureSummaryList?.[name]?.descriptionItems !==
                      curValues.featureSummaryList?.[name]?.descriptionItems
                    }
                  >
                    {({ getFieldValue }) => {
                      const format = getFieldValue([
                        "featureSummaryList",
                        name,
                        "descriptionFormat",
                      ]);
                      const description = getFieldValue([
                        "featureSummaryList",
                        name,
                        "description",
                      ]);
                      const items = getFieldValue([
                        "featureSummaryList",
                        name,
                        "descriptionItems",
                      ]) || [];

                      const hasTooltip = format && (
                        (format === "paragraph" && description) ||
                        (format === "list" && items.length > 0)
                      );

                      const tooltipContent = (
                        <div style={{ width: 320 }}>
                          <Text strong style={{ display: "block", marginBottom: 12 }}>
                            Info Tooltip
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 12 }}>
                            This info will appear when users hover the ⓘ icon on the plan card.
                          </Text>
                          
                          <Form.Item
                            {...restField}
                            name={[name, "descriptionFormat"]}
                            label="Format"
                            style={{ marginBottom: 12 }}
                          >
                            <Select 
                              placeholder="Select format" 
                              allowClear
                              style={{ width: "100%" }}
                            >
                              <Option value="paragraph">
                                <Space>
                                  <span>📝</span> Text (paragraph)
                                </Space>
                              </Option>
                              <Option value="list">
                                <Space>
                                  <span>📋</span> List (bullet points)
                                </Space>
                              </Option>
                            </Select>
                          </Form.Item>

                          {format === "paragraph" && (
                            <Form.Item
                              {...restField}
                              name={[name, "description"]}
                              label="Description"
                              style={{ marginBottom: 0 }}
                            >
                              <TextArea
                                placeholder="E.g., Supports SQL Server, PostgreSQL, MySQL and more..."
                                rows={3}
                              />
                            </Form.Item>
                          )}

                          {format === "list" && (
                            <Form.List name={[name, "descriptionItems"]}>
                              {(itemFields, { add: addItem, remove: removeItem }) => (
                                <div>
                                  <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: "block" }}>
                                    List items:
                                  </Text>
                                  {itemFields.map((itemField, index) => (
                                    <Flex key={itemField.key} gap={8} align="center" style={{ marginBottom: 8 }}>
                                      <span style={{ color: "#888", width: 16 }}>•</span>
                                      <Form.Item
                                        {...itemField}
                                        style={{ marginBottom: 0, flex: 1 }}
                                        rules={[{ required: true, message: "Required" }]}
                                      >
                                        <Input placeholder={`Item ${index + 1}`} />
                                      </Form.Item>
                                      <Button
                                        type="text"
                                        danger
                                        size="small"
                                        icon={<MinusCircleOutlined />}
                                        onClick={() => removeItem(itemField.name)}
                                      />
                                    </Flex>
                                  ))}
                                  <Button
                                    type="dashed"
                                    onClick={() => addItem("")}
                                    icon={<PlusOutlined />}
                                    block
                                    size="small"
                                  >
                                    Add Item
                                  </Button>
                                </div>
                              )}
                            </Form.List>
                          )}
                        </div>
                      );

                      return (
                        <Popover
                          content={tooltipContent}
                          title={null}
                          trigger="click"
                          placement="bottomRight"
                        >
                          <Button
                            type={hasTooltip ? "primary" : "text"}
                            ghost={hasTooltip}
                            size="small"
                            icon={hasTooltip ? <EditOutlined /> : <InfoCircleOutlined />}
                            style={{ 
                              color: hasTooltip ? "#1890ff" : "rgba(0,0,0,0.45)",
                              borderColor: hasTooltip ? "#1890ff" : "transparent"
                            }}
                          >
                            {hasTooltip ? "Edit Info" : "Add Info"}
                          </Button>
                        </Popover>
                      );
                    }}
                  </Form.Item>
                </div>
              ))}
            </>
          )}
        </Form.List>
      </div>
    </div>
  );
};

export default PlanEditor;
